import { BdLabelColorDirective } from '@bindoc-projects/labels-lib/ui';
import { DatePipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ShareableLabel } from '@bindoc-projects/resource-domain-lib';
import {
  BD_SHAREABLE_NAME_RESOLVERS,
  ShareableNameResolver,
  ShareableNameResolvers,
} from '../../select/global-search/shareable-name-resolver';
import { MenuItem, SortEvent } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { AvatarGroup } from 'primeng/avatargroup';
import { Button, ButtonDirective, ButtonIcon } from 'primeng/button';
import { Chip } from 'primeng/chip';
import { Listbox } from 'primeng/listbox';
import { Menu } from 'primeng/menu';
import { Popover } from 'primeng/popover';
import { Skeleton } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';

import { BdUiInlineNotificationComponent } from '@bindoc-projects/ui-lib/feedback/ui';

import {
  FolderItem,
  FolderTablePresentation,
  InlineNotice,
  SHAREABLE_LIST_BROWSE,
} from '../shareables-overview.models';

import {
  type PermissionAvatar,
  type TypeFilterOption,
  typeFilterOptionsFor,
  permissionAvatarsFor,
  editingPermissionAvatarsFor,
  type FolderTableSortBy,
  type FolderTableSortMode,
  type FolderTableSortChange,
  sortFolderItemsBy,
  sortFolderItems,
  sortFolderItemsByModifiedDate,
  sameTypeFilterValues,
  filterFolderItems,
  selectableFolderItems,
  visibleFolderSelection,
  mergeFolderSelection,
  toggleFolderSelection,
} from './folder-table.logic';

// Preserve existing imports while new logic consumers avoid loading the component.
export {
  type PermissionAvatar,
  type TypeFilterOption,
  typeFilterOptionsFor,
  permissionAvatarsFor,
  editingPermissionAvatarsFor,
  type FolderTableSortBy,
  type FolderTableSortMode,
  type FolderTableSortChange,
  compareFolderItemsByField,
  sortFolderItemsBy,
  sortFolderItems,
  sortFolderItemsByModifiedDate,
} from './folder-table.logic';

const FILTER_THRESHOLD = 3;

@Component({
  selector: 'bd-folder-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (showParentRow()) {
      <button
        type="button"
        class="bd-folder-table__parent-row"
        (click)="parentNavigate.emit()"
      >
        <i class="pi pi-arrow-up"></i>
        <span>Übergeordneter Ordner</span>
      </button>
    }
    @if (notice(); as notice) {
      <bd-ui-inline-notification
        [severity]="notice.severity"
        [title]="notice.title"
        [message]="notice.message ?? ''"
      ></bd-ui-inline-notification>
    } @else if (!loading() && sortedItems().length === 0) {
      @if (typeFilterActive()) {
        <!--
          The Typ filter left nothing — not even a folder — so the chip strip in
          the caption went away with the table. This notice takes over as the
          state, and carries the way out.
        -->
        <!--
          The Typ filter is this table's own, so it names itself in the title —
          but it is rarely the only thing narrowing the list. The empty detail
          is where the parent says what else is active (a label, a search), so
          the way out is not mistaken for the Typ filter alone.
        -->
        <bd-ui-inline-notification
          severity="info"
          icon="pi pi-filter"
          [title]="typeFilterEmptyTitle()"
          [message]="emptyDetail()"
          actionLabel="Filter zurücksetzen"
          (action)="clearTypeFilter()"
        ></bd-ui-inline-notification>
      } @else {
        <bd-ui-inline-notification
          severity="info"
          [icon]="emptyIcon()"
          [title]="emptyMessage()"
          [message]="emptyDetail()"
        ></bd-ui-inline-notification>
      }
    } @else {
      <!--
        Narrow-layout controls for what the collapsed column headers own on wide
        layouts. The sortable headers other than "Name" are hidden below 40rem,
        which would otherwise strand the list on whatever order it loaded with,
        and the Typ header takes its filter trigger down with it. Rendered (not
        @if-ed on width) so the container query alone decides visibility.
      -->
      @if (sortable() || typeFilterVisible() || labelFilterVisible()) {
        <div class="bd-folder-table__sort">
          @if (labelFilterVisible()) {
            <button
              pButton
              text
              size="small"
              severity="secondary"
              type="button"
              aria-haspopup="dialog"
              [attr.aria-label]="labelFilterAriaLabel()"
              [attr.aria-expanded]="labelFilterOpen()"
              data-test="folder-table-label-filter-narrow"
              (click)="labelFilterPopover.toggle($event)"
            >
              <i [class]="labelFilterIcon()" aria-hidden="true"></i
              ><span>{{ labelFilterNarrowLabel() }}</span>
            </button>
          }
          @if (typeFilterVisible()) {
            <button
              pButton
              text
              size="small"
              severity="secondary"
              type="button"
              aria-haspopup="dialog"
              [attr.aria-label]="typeFilterAriaLabel()"
              [attr.aria-expanded]="typeFilterOpen()"
              data-test="folder-table-type-filter-narrow"
              (click)="typeFilterPopover.toggle($event)"
            >
              <i [class]="typeFilterIcon()" aria-hidden="true"></i
              ><span>{{ typeFilterNarrowLabel() }}</span>
            </button>
          }
          @if (sortable()) {
            <button
              pButton
              text
              size="small"
              severity="secondary"
              aria-haspopup="menu"
              (click)="sortMenu.toggle($event)"
            >
              <i class="pi pi-sort-alt" aria-hidden="true"></i
              ><span>{{ activeSortLabel() }}</span>
            </button>
            <p-menu
              #sortMenu
              [model]="sortMenuItems()"
              [popup]="true"
              appendTo="body"
            />
          }
        </div>
      }
      <!--
        One popover for both triggers (header cell and narrow strip), declared
        at this branch's top level: an embedded view can reach a template
        reference of its parent view, never the other way round, so anchoring it
        inside either trigger's @if would put it out of the other's reach.
        appendTo="body" is required — the default 'self' would render the panel
        inside the scrolling table container, which clips it.
      -->
      <p-popover
        #typeFilterPopover
        appendTo="body"
        (onShow)="typeFilterOpen.set(true)"
        (onHide)="typeFilterOpen.set(false)"
      >
        <div class="bd-folder-table__column-filter-panel">
          <p-listbox
            [options]="typeFilterOptions()"
            optionLabel="label"
            optionValue="label"
            [multiple]="true"
            [checkbox]="true"
            [metaKeySelection]="false"
            [showToggleAll]="false"
            [filter]="typeFilterSearchable()"
            filterBy="label"
            filterPlaceHolder="Typ suchen…"
            ariaLabel="Typ"
            scrollHeight="16rem"
            [ngModel]="typeFilterValues()"
            (ngModelChange)="setTypeFilter($event)"
          >
            <ng-template #item let-option>
              <!--
                data-test sits on this span, not on the <li>: the list box's
                "option" pass-through hands its function only
                { selected, focused, disabled } (see getPTOptions in
                primeng/listbox), never the option itself, so the value cannot be
                read there.
              -->
              <span
                class="bd-folder-table__type-filter-option"
                [attr.data-test]="
                  'folder-table-type-filter-option-' + option.label
                "
              >
                <span>{{ option.label }}</span>
                <span class="bd-folder-table__type-filter-count">{{
                  option.count
                }}</span>
              </span>
            </ng-template>
          </p-listbox>
          @if (typeFilterActive()) {
            <div class="flex justify-end">
              <!--
                data-test through [pt] root: on p-button that is the inner
                <button>, which is what Playwright's actionability check needs.
              -->
              <p-button
                label="Zurücksetzen"
                [text]="true"
                severity="secondary"
                size="small"
                [pt]="{
                  root: { 'data-test': 'folder-table-type-filter-reset' },
                }"
                (onClick)="clearTypeFilter()"
              ></p-button>
            </div>
          }
        </div>
      </p-popover>
      <!--
        The Label filter's panel. Single-select on purpose: the value is handed
        up to the parent, which re-requests the list with it (the vocabulary
        comes from the label endpoint, not from the loaded rows), and the API
        takes a list without defining how several labels combine.
      -->
      <p-popover
        #labelFilterPopover
        appendTo="body"
        (onShow)="labelFilterOpen.set(true)"
        (onHide)="labelFilterOpen.set(false)"
      >
        <div class="bd-folder-table__column-filter-panel">
          <p-listbox
            [options]="labelFilterOptionItems()"
            optionLabel="label"
            optionValue="label"
            [metaKeySelection]="false"
            [filter]="labelFilterSearchable()"
            filterBy="label"
            filterPlaceHolder="Label suchen…"
            ariaLabel="Label"
            scrollHeight="16rem"
            [ngModel]="labelFilterValue()"
            (ngModelChange)="setLabelFilter($event)"
          >
            <ng-template #item let-option>
              <!-- data-test on the span, see the Typ filter's option template. -->
              <span
                [attr.data-test]="
                  'folder-table-label-filter-option-' + option.label
                "
                >{{ option.label }}</span
              >
            </ng-template>
          </p-listbox>
          @if (labelFilterActive()) {
            <div class="flex justify-end">
              <p-button
                label="Zurücksetzen"
                [text]="true"
                severity="secondary"
                size="small"
                [pt]="{
                  root: { 'data-test': 'folder-table-label-filter-reset' },
                }"
                (onClick)="clearLabelFilter()"
              ></p-button>
            </div>
          }
        </div>
      </p-popover>
      <p-table
        [value]="tableValue()"
        [loading]="loading()"
        [showLoader]="false"
        [selection]="effectiveSelection()"
        [rowSelectable]="rowSelectable"
        size="small"
        [scrollable]="fillHeight()"
        [scrollHeight]="scrollHeight()"
        [virtualScroll]="virtualScroll()"
        [virtualScrollItemSize]="41"
        [paginator]="paginate() && !loading()"
        [rows]="pageSize()"
        [showCurrentPageReport]="paginate() && !loading()"
        currentPageReportTemplate="{first} - {last} von {totalRecords}"
        stripedRows
        [customSort]="sortable()"
        [sortField]="activeSortField()"
        [sortOrder]="sortOrder()"
        (sortFunction)="onSortFunction($event)"
        (selectionChange)="onTableSelectionChange($event)"
      >
        <!--
          The active filter, above the rows. Rendered unconditionally with the
          @if *inside*: PrimeNG resolves the caption through a ContentChild once,
          so wrapping the whole <ng-template> in a condition would leave the
          table without a caption for the rest of its life. The wrapper the
          table draws around it is hidden by CSS while no filter is active (see
          the .p-datatable-header rule below).
        -->
        <ng-template #caption>
          @if (typeFilterActive() || labelFilterActive()) {
            <div class="flex flex-wrap items-center gap-2">
              @if (labelFilterActive()) {
                <p-chip
                  [label]="labelFilterChipLabel()"
                  [removable]="true"
                  [pt]="{
                    root: { 'data-test': 'folder-table-label-filter-chip' },
                    removeIcon: {
                      'aria-label': labelFilterChipLabel() + ' entfernen',
                    },
                  }"
                  (onRemove)="clearLabelFilter()"
                ></p-chip>
              }
              @for (value of typeFilterValues(); track value) {
                <p-chip
                  [label]="'Typ: ' + value"
                  [removable]="true"
                  [pt]="{
                    root: {
                      'data-test': 'folder-table-type-filter-chip-' + value,
                    },
                    removeIcon: {
                      'aria-label': 'Filter Typ: ' + value + ' entfernen',
                    },
                  }"
                  (onRemove)="removeTypeFilterValue(value)"
                ></p-chip>
              }
              @if (typeFilterValues().length > 1) {
                <p-button
                  label="Alle zurücksetzen"
                  [text]="true"
                  severity="secondary"
                  size="small"
                  (onClick)="clearTypeFilter()"
                ></p-button>
              }
            </div>
          }
        </ng-template>
        <ng-template #header>
          <tr>
            @if (showCheckbox()) {
              <th class="w-10 bd-folder-table__col--checkbox">
                <p-table-header-checkbox></p-table-header-checkbox>
              </th>
            }
            <th class="bd-folder-table__col--icon"></th>
            <th
              class="bd-folder-table__col--name"
              pSortableColumn="name"
              [pSortableColumnDisabled]="!sortable() || loading()"
            >
              Name
              @if (sortable()) {
                <p-sort-icon field="name"></p-sort-icon>
              }
              @if (labelFilterVisible()) {
                <!--
                  Labels are rendered as tags inside this column's cells, so the
                  filter over them belongs to this header — same reason the Typ
                  filter sits in the Typ header. The interaction-stopping
                  wrapper is required for the same reason as there: the <th> is
                  itself the sort control.
                -->
                <span
                  class="bd-folder-table__column-filter"
                  (click)="$event.stopPropagation()"
                  (keydown.enter)="$event.stopPropagation()"
                  (keydown.space)="$event.stopPropagation()"
                >
                  <button
                    pButton
                    text
                    rounded
                    size="small"
                    severity="secondary"
                    type="button"
                    [class.bd-folder-table__column-filter--active]="
                      labelFilterActive()
                    "
                    aria-haspopup="dialog"
                    [attr.aria-label]="labelFilterAriaLabel()"
                    [attr.aria-expanded]="labelFilterOpen()"
                    data-test="folder-table-label-filter"
                    (click)="labelFilterPopover.toggle($event)"
                  >
                    <i [class]="labelFilterIcon()" pButtonIcon></i>
                  </button>
                </span>
              }
            </th>
            @if (showType()) {
              <th
                class="bd-folder-table__col--type"
                [class.w-32]="!typeFilterVisible()"
                [class.w-40]="typeFilterVisible()"
                pSortableColumn="type"
                [pSortableColumnDisabled]="!sortable() || loading()"
              >
                Typ
                @if (sortable()) {
                  <p-sort-icon field="type"></p-sort-icon>
                }
                @if (typeFilterVisible()) {
                  <!--
                    The <th> itself is the sort control: pSortableColumn sorts on
                    a host click *and* on Enter/Space. Every interaction of the
                    nested trigger therefore has to stop here, or opening the
                    filter would re-sort the column underneath it.
                  -->
                  <span
                    class="bd-folder-table__column-filter"
                    (click)="$event.stopPropagation()"
                    (keydown.enter)="$event.stopPropagation()"
                    (keydown.space)="$event.stopPropagation()"
                  >
                    <button
                      pButton
                      text
                      rounded
                      size="small"
                      severity="secondary"
                      type="button"
                      [class.bd-folder-table__column-filter--active]="
                        typeFilterActive()
                      "
                      aria-haspopup="dialog"
                      [attr.aria-label]="typeFilterAriaLabel()"
                      [attr.aria-expanded]="typeFilterOpen()"
                      data-test="folder-table-type-filter"
                      (click)="typeFilterPopover.toggle($event)"
                    >
                      <i [class]="typeFilterIcon()" pButtonIcon></i>
                      @if (typeFilterActive()) {
                        <span class="bd-folder-table__column-filter-badge">{{
                          typeFilterValues().length
                        }}</span>
                      }
                    </button>
                  </span>
                }
              </th>
            }
            @if (showPermissions()) {
              <th class="w-28 bd-folder-table__col--permissions"></th>
            }
            <th
              class="w-36 bd-folder-table__col--date"
              pSortableColumn="modifiedDate"
              [pSortableColumnDisabled]="!sortable() || loading()"
            >
              Geändert
              @if (sortable()) {
                <p-sort-icon field="modifiedDate"></p-sort-icon>
              }
            </th>
            <th class="w-10 bd-folder-table__col--menu"></th>
          </tr>
        </ng-template>
        <ng-template #body let-item>
          <tr
            [attr.data-row-id]="item.id"
            [class.bd-folder-table__row--highlighted]="
              item.id === highlightedId()
            "
          >
            @if (showCheckbox()) {
              <td class="bd-folder-table__col--checkbox">
                <!--
                  In picker mode folders are navigation, not pickable resources
                  (the Finder model), so their checkbox is dropped — the cell
                  itself stays so the columns keep lining up. See the
                  folderCheckbox input.
                -->
                @if (folderCheckbox() || !item.isFolder) {
                  <p-table-checkbox [value]="item"></p-table-checkbox>
                }
              </td>
            }
            <td class="bd-folder-table__col--icon">
              <i [class]="iconFor(item)"></i>
            </td>
            <td
              (click)="onItemClick(item)"
              class="cursor-pointer bd-folder-table__col--name"
            >
              <span class="inline-flex items-center gap-2 flex-wrap">
                <!--
                  The whole cell stays clickable for pointer users, but the name
                  is a real button so the row's primary action is focusable and
                  Enter/Space-activatable (the cell alone is not). Its click is
                  stopped from bubbling to the cell handler to avoid a double
                  navigate.
                -->
                <button
                  type="button"
                  class="bd-folder-table__name"
                  (click)="onNameClick($event, item)"
                >
                  {{ displayName(item) }}
                </button>
                @for (label of item.labels; track label.text) {
                  <p-tag
                    [value]="label.text"
                    [bdLabelColor]="label.color"
                    [severity]="label.color ? null : 'secondary'"
                    icon="pi pi-tag"
                    class="cursor-pointer"
                    (click)="onLabelClick($event, label)"
                  />
                }
              </span>
              <!--
                Compact stand-in for the columns collapsed on narrow containers
                (see the container query below): type, modified date and the
                permission glyphs move under the name as a muted meta line.
                Hidden on wide layouts.
              -->
              <span class="bd-folder-table__meta">
                @if (showType()) {
                  <span>{{ item.typeLabel }}</span>
                }
                @if (item.modifiedDate) {
                  <span>{{ item.modifiedDate | date: 'short' }}</span>
                }
                @if (showPermissions()) {
                  @if (getEditingPermissionAvatars(item); as avatars) {
                    @if (avatars.length > 0) {
                      <span
                        class="bd-folder-table__meta-permissions"
                        [attr.aria-label]="editingPermissionsLabel(item)"
                      >
                        @for (a of avatars; track a.permission) {
                          <i [class]="a.icon" aria-hidden="true"></i>
                        }
                      </span>
                    }
                  }
                }
              </span>
            </td>
            @if (showType()) {
              <td class="bd-folder-table__col--type">{{ item.typeLabel }}</td>
            }
            @if (showPermissions()) {
              <td class="bd-folder-table__col--permissions">
                @if (getPermissionAvatars(item); as avatars) {
                  @if (avatars.length > 0) {
                    <p-avatar-group>
                      @for (a of avatars; track a.permission) {
                        <p-avatar
                          [icon]="a.icon"
                          [pTooltip]="a.tooltip"
                          tooltipPosition="left"
                          shape="circle"
                          size="normal"
                        />
                      }
                    </p-avatar-group>
                  }
                }
              </td>
            }
            <td class="bd-folder-table__col--date">
              {{ item.modifiedDate | date: 'short' }}
            </td>
            <td class="bd-folder-table__col--menu">
              @if (menuItemsProvider()(item).length > 0) {
                <button
                  pButton
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  aria-haspopup="menu"
                  [attr.aria-label]="'Aktionen für ' + displayName(item)"
                  (click)="onRowMenu($event, item)"
                >
                  <i class="pi pi-ellipsis-v" pButtonIcon></i>
                </button>
              }
            </td>
          </tr>
        </ng-template>
        <!--
          Rendered instead of #body while loading is true — PrimeNG swaps in
          this whole template (once, not per row) rather than iterating #body
          over placeholder data. Kept as ONE persistent p-table across the
          loading/loaded transition (not a second, separately-mounted
          p-table, as an earlier version had it): a fresh p-table instance
          resets PrimeNG's internal sort state before it re-syncs from our
          sortField/sortOrder bindings, which raced the very first
          (customSort) sort emission and silently flipped the default and
          every click's result to ascending (Klia "Konversationen" sorting
          regression).

          PrimeNG's own loading mask (grey overlay plus spinner) is switched
          off via showLoader=false, so these skeleton rows are the only
          loading indicator.

          Mirrors #header's columns exactly so cells stay aligned;
          skeletonRows only sets the placeholder row count (3). No backticks
          in this comment — it lives inside the component's own template
          literal, and a backtick here would close that string early.
        -->
        <ng-template #loadingbody>
          @for (row of skeletonRows; track row) {
            <tr>
              @if (showCheckbox()) {
                <td class="bd-folder-table__col--checkbox"></td>
              }
              <td class="bd-folder-table__col--icon">
                <p-skeleton width="20px" height="20px"></p-skeleton>
              </td>
              <td class="bd-folder-table__col--name">
                <p-skeleton width="100%"></p-skeleton>
              </td>
              @if (showType()) {
                <td class="bd-folder-table__col--type">
                  <p-skeleton width="100%"></p-skeleton>
                </td>
              }
              @if (showPermissions()) {
                <td class="bd-folder-table__col--permissions">
                  <p-skeleton width="20px" height="20px"></p-skeleton>
                </td>
              }
              <td class="bd-folder-table__col--date">
                <p-skeleton width="100%"></p-skeleton>
              </td>
              <td class="bd-folder-table__col--menu">
                <p-skeleton width="20px" height="20px"></p-skeleton>
              </td>
            </tr>
          }
        </ng-template>
      </p-table>
      <p-menu
        #rowMenu
        [model]="activeRowMenuItems()"
        [popup]="true"
        appendTo="body"
      ></p-menu>
    }
  `,
  host: {
    class: 'flex flex-col',
    // Only a height-filling table takes the free space and shrinks below its
    // content; see `fillHeight`.
    '[class.flex-auto]': 'fillHeight()',
    '[class.min-h-0]': 'fillHeight()',
    '[class.bd-folder-table--uniform-icons]': 'hasUniformIcons()',
    '[class.bd-folder-table--sortable]': 'sortable()',
    '[class.bd-folder-table--has-checkbox]': 'showCheckbox()',
    '[class.bd-folder-table--filter-active]':
      'typeFilterActive() || labelFilterActive()',
    // Esc closes an open column filter wherever focus is. PrimeNG's popover
    // only dismisses on an outside click, and the list box keeps Escape to
    // itself, so without this a keyboard user could open a filter but not
    // leave it.
    '(document:keydown.escape)': 'closeColumnFilters()',
  },
  styles: [
    // Let the scrollable container grow inside the host's flex column so the
    // table's scrollHeight="flex" has a height to fill. PrimeNG's scrollable
    // mode already pins the header (sticky <thead>), so no sticky override is
    // needed here.
    //
    // The sorted column header gets a translucent "selected" highlight
    // (Material: color-mix(..., transparent 88%)). While the header is pinned,
    // the rows scrolling underneath show through that one cell, so back the
    // highlight with the opaque base header background to keep it readable.
    `
      /*
       * The table adapts to the width of the component, not the viewport, so
       * it also behaves inside split layouts and dialogs. inline-size
       * containment makes :host a query container for the rules below.
       */
      :host {
        container-type: inline-size;
        container-name: bd-folder-table;
      }
      /* Stand-in meta line for collapsed columns — wide layouts hide it. */
      .bd-folder-table__meta {
        display: none;
      }
      /* Narrow-layout-only sort control; the column headers own this on wide. */
      .bd-folder-table__sort {
        display: none;
      }
      /* A column header's filter trigger, right of the sort icon. */
      .bd-folder-table__column-filter {
        display: inline-flex;
        align-items: center;
        margin-inline-start: 0.25rem;
      }
      /*
       * Active filter: the trigger becomes a small pill carrying how many
       * values are picked, so the state is legible without opening the panel.
       */
      .bd-folder-table__column-filter--active {
        background: light-dark(
          var(--p-primary-50),
          var(--p-highlight-background)
        );
        color: var(--p-text-color);
        border-radius: 999px;
      }
      .bd-folder-table__column-filter-badge {
        font-size: 0.75rem;
        font-weight: 700;
        line-height: 1;
      }
      /*
       * The caption wrapper exists as soon as a #caption template does, so the
       * inactive state would draw an empty bar above the rows. The host class
       * is the only thing that knows the difference (see the template comment).
       */
      :host:not(.bd-folder-table--filter-active) ::ng-deep .p-datatable-header {
        display: none;
      }
      /*
       * Filter panel content. Declared in this component's template, so it keeps
       * this component's encapsulation attributes even though the popover is
       * appended to <body> — no ::ng-deep needed.
       */
      .bd-folder-table__column-filter-panel {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        min-width: 14rem;
      }
      .bd-folder-table__type-filter-option {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
      }
      /* Tabular figures so the counts line up down the column. */
      .bd-folder-table__type-filter-count {
        margin-left: auto;
        font-variant-numeric: tabular-nums;
        color: var(--p-text-muted-color);
        font-size: 0.75rem;
      }
      /*
       * The name is a <button> so it can take focus and Enter/Space, but it must
       * read as the row's text: strip the control chrome and inherit type. The
       * focus ring is kept (and is the only reason the reset is safe).
       */
      .bd-folder-table__name {
        padding: 0;
        border: none;
        background: none;
        font: inherit;
        color: inherit;
        text-align: start;
        cursor: pointer;
      }
      @container bd-folder-table (max-width: 40rem) {
        /*
         * Narrow layout (smartphones, slim panes): only Name and the row menu
         * remain as columns. Type, modified date and the user's permissions
         * (as compact glyphs instead of the avatar group) move into the meta
         * line under the name.
         */
        :host ::ng-deep .bd-folder-table__col--type,
        :host ::ng-deep .bd-folder-table__col--permissions,
        :host ::ng-deep .bd-folder-table__col--date {
          display: none;
        }
        /*
         * A flat list shows the same glyph on every row, so the 3rem icon
         * column is decoration the name can use. A list containing folders
         * keeps it — there the glyph is the only folder/file distinction.
         */
        :host(.bd-folder-table--uniform-icons)
          ::ng-deep
          .bd-folder-table__col--icon {
          display: none;
        }
        .bd-folder-table__sort {
          display: flex;
          justify-content: flex-end;
          flex: none;
          margin-bottom: 0.25rem;
        }
        /*
         * With the sort button present, a header row whose only remaining
         * column is "Name" would be a second, weaker sort affordance for one of
         * the four orders — and the button already names the active one in
         * words. Drop it and give the rows the vertical space. Not when the
         * checkbox column is on: there the header also carries the select-all,
         * which the sort button does not replace.
         */
        :host(.bd-folder-table--sortable):not(.bd-folder-table--has-checkbox)
          ::ng-deep
          .p-datatable-thead {
          display: none;
        }
        .bd-folder-table__meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 0.125rem;
          font-size: 0.75rem;
          color: var(--p-text-muted-color);
        }
        /* "Typ · Datum · Rechte" separator, only between rendered parts. */
        .bd-folder-table__meta > span + span::before {
          content: '·';
          margin-inline-end: 0.5rem;
        }
        .bd-folder-table__meta-permissions {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
        }
        /* The '·' becomes a flex item here; the flex gap already spaces it. */
        .bd-folder-table__meta-permissions::before {
          margin-inline-end: 0;
        }
        .bd-folder-table__meta-permissions i {
          font-size: 0.75rem;
        }
        /* Keep long unbroken names (URLs, file names) inside the column. */
        :host ::ng-deep td.bd-folder-table__col--name {
          overflow-wrap: anywhere;
        }
        /*
         * The "{first} - {last} von {totalRecords}" report makes the pager
         * wider than a phone; the page links already carry that information.
         */
        :host ::ng-deep .p-paginator .p-paginator-current {
          display: none;
        }
      }
      :host ::ng-deep .p-datatable-scrollable {
        flex: 1 1 auto;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-scrollable > .p-datatable-table-container {
        flex: 1 1 auto;
        min-height: 0;
      }
      :host
        ::ng-deep
        .p-datatable-scrollable
        .p-datatable-thead
        > tr
        > th.p-datatable-column-sorted {
        background:
          linear-gradient(
            var(--p-datatable-header-cell-selected-background),
            var(--p-datatable-header-cell-selected-background)
          ),
          var(--p-datatable-header-cell-background);
      }
      /*
       * Icon column: give the leading folder/file glyph a comfortable, even
       * gutter instead of sharing the generic w-10 spacer width. PrimeNG's own
       * cell rule (.p-datatable-t{head,body} > tr > td|th, specificity 0,1,2)
       * sets the cell's text-align and horizontal padding, so we scope our
       * override under the same cell context (0,2,2) to win — a bare
       * .bd-folder-table__col--icon (0,1,0) would lose and leave the glyph
       * left-aligned. Zeroing the inline padding makes the 3rem width the only
       * horizontal box, so text-align:center sits the glyph dead-center with
       * equal space on both sides.
       */
      :host ::ng-deep .p-datatable-thead > tr > th.bd-folder-table__col--icon,
      :host ::ng-deep .p-datatable-tbody > tr > td.bd-folder-table__col--icon {
        width: 3rem;
        padding-inline: 0;
        text-align: center;
      }
      :host ::ng-deep .bd-folder-table__col--icon i {
        font-size: 1.125rem;
      }
      .bd-folder-table__parent-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 0 0 auto;
        width: 100%;
        padding: 0.375rem 0.5rem;
        border: none;
        border-bottom: 1px solid var(--p-datatable-body-cell-border-color);
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: start;
        cursor: pointer;
      }
      .bd-folder-table__parent-row:hover {
        background: var(--p-datatable-row-hover-background);
      }
      /*
       * Deep-link arrival highlight. Reuses the Material "selected row"
       * background token so the flash blends with the table; the inset ring
       * gives it edges that read against striped rows. The animation runs
       * the full 2.5s the parent keeps the highlightedId set, then the
       * class goes away with the input change.
       */
      :host ::ng-deep .bd-folder-table__row--highlighted > td {
        animation: bd-folder-table-row-highlight 2.5s ease-out;
      }
      @keyframes bd-folder-table-row-highlight {
        0% {
          background: var(--p-datatable-row-selected-background);
          box-shadow: inset 0 0 0 2px var(--p-primary-color);
        }
        100% {
          background: transparent;
          box-shadow: inset 0 0 0 0 transparent;
        }
      }
    `,
  ],
  imports: [
    BdLabelColorDirective,
    DatePipe,
    FormsModule,
    TableModule,
    Button,
    ButtonDirective,
    ButtonIcon,
    Avatar,
    AvatarGroup,
    Chip,
    Listbox,
    Menu,
    Popover,
    Skeleton,
    Tag,
    Tooltip,
    BdUiInlineNotificationComponent,
  ],
})
export class BdFolderTableComponent {
  public readonly items = input<FolderItem[]>([]);
  /**
   * Externally controlled selection. When set — even to `[]` — the tick state
   * is *derived* from it instead of being owned by the table: on every `items`
   * or `selection` change the ticks are the subset of the listed rows whose
   * `type`+`id` appear in it. That is what lets a picker keep its picks while
   * the user walks through folders or the list refetches (fresh objects, same
   * ids). Left `undefined`, the table owns its selection as before.
   */
  public readonly selection = input<readonly FolderItem[] | undefined>(
    undefined,
  );
  /**
   * The mode flags — checkbox column, folder selectability, permissions column —
   * as one object, so call sites hand down a preset
   * ({@link SHAREABLE_LIST_BROWSE} / `SHAREABLE_LIST_PICK`) instead of a row of
   * booleans. See {@link FolderTablePresentation} for why these three and not
   * the other flags below.
   */
  public readonly presentation = input<FolderTablePresentation>(
    SHAREABLE_LIST_BROWSE,
  );
  protected readonly showCheckbox = computed(
    () => this.presentation().showCheckbox,
  );
  /**
   * See {@link FolderTablePresentation.folderCheckbox} and
   * {@link rowSelectable}. An active Typ filter suspends it: the rows shown are
   * then a narrowed set of files, and a folder that is only listed because
   * folders are never filtered away must not be swept up by a select-all that
   * reads as "everything matching the filter".
   */
  protected readonly folderCheckbox = computed(
    () => this.presentation().folderCheckbox && !this.typeFilterActive(),
  );
  public readonly showType = input(false);
  /**
   * Whether the Typ column header offers a value filter (and, on narrow
   * containers where that header is hidden, the sort strip does).
   *
   * Opt-in, because it only makes sense where this table sees the whole list.
   * That is not the same as sorting client-side: the recent tab sorts
   * server-side (`parent` mode) yet walks *every* server page into `items`, so
   * it filters and counts over the full set and does opt in. A list capped to
   * one page (the recent tab under `limit`, e.g. Home's 20 newest) does not —
   * there the filter would narrow that page alone and report its counts as the
   * whole truth. It is also off wherever the surface around the table already
   * offers a type filter of its own — the resource picker's criteria popover.
   */
  public readonly typeFilter = input(false);
  /**
   * Whether the Name column header offers the Label filter.
   *
   * Unlike the Typ filter this table does not *apply* it: the value is reported
   * through {@link labelFilterChange} and the parent re-requests the list with
   * it, because label filtering runs server-side across every page — the rows
   * that arrive are already filtered. So this table only owns the control, and
   * {@link labelFilterOptions} is the vocabulary to offer (which is the label
   * endpoint's answer, wider than the labels visible on the loaded rows).
   */
  public readonly labelFilter = input(false);
  /** The label texts to offer; empty hides the filter unless one is active. */
  public readonly labelFilterOptions = input<readonly string[]>([]);
  /** The label currently filtered on, or `null` for "no filter". */
  public readonly labelFilterValue = input<string | null>(null);
  /** A label picked or cleared in the Name column's filter. */
  public readonly labelFilterChange = output<string | null>();
  protected readonly showPermissions = computed(
    () => this.presentation().showPermissions,
  );
  public readonly loading = input(false);
  /**
   * Persistent state shown instead of the table when the content could not be
   * loaded or may not be opened. Takes precedence over {@link emptyMessage} —
   * a failed load must never read as "this folder is empty".
   */
  public readonly notice = input<InlineNotice | null>(null);
  /**
   * Title of the empty state. An empty list is an inline `info` state, so this
   * is a concise statement without trailing punctuation, not a sentence.
   */
  public readonly emptyMessage = input('Keine Einträge vorhanden');
  /** Optional plain-language explanation under {@link emptyMessage}. */
  public readonly emptyDetail = input('');
  /**
   * Optional glyph override for the empty state. The styleguide keeps an empty
   * **search** neutral but marks it with a search icon; every other empty state
   * keeps the default info circle.
   */
  public readonly emptyIcon = input('');
  /** Fixed order of a non-sortable list — see {@link FolderTableSortMode}. */
  public readonly sortBy = input<FolderTableSortBy>('name');
  /**
   * Whether the Name/Typ/Geändert columns can be sorted, and who applies the
   * order — see {@link FolderTableSortMode}. `fixed` (the default) keeps the
   * headers inert.
   */
  public readonly sortMode = input<FolderTableSortMode>('fixed');
  /**
   * The sorted column and its direction. A `model` because in `client` mode the
   * table owns this state (a header click writes it); in `parent` mode the
   * owner binds it one-way and drives it from {@link sortChange} instead.
   *
   * The defaults are the folder-listing default order (see
   * {@link sortFolderItems}), which is why it needs no separate reset control.
   */
  public readonly sortField = model<FolderTableSortBy>('name');
  public readonly sortOrder = model<1 | -1>(1);
  /**
   * Page size. When set, a pager is shown with this many rows per page
   * (mutually exclusive with the default virtual scrolling); when omitted the
   * table virtual-scrolls the full list.
   */
  public readonly pageSize = input<number | undefined>(undefined);
  /** Show a "go up one level" link as the first row (folder browsing). */
  public readonly showParentRow = input(false);
  /**
   * Whether the table claims the height its parent offers and scrolls its rows
   * internally (sticky header, pager pinned to the bottom). This is the right
   * mode wherever the list *is* the page — a folder browser, a file center.
   *
   * Set to `false` when the list is one section among others on a page that
   * scrolls as a whole (the Home dashboard): the table then renders at its
   * content height instead of being squeezed into whatever vertical space is
   * left over, which on a short viewport is a sliver of two or three rows with
   * a second scrollbar inside the page.
   */
  public readonly fillHeight = input(true);
  /**
   * Item id to flash + scroll into view after a deep-link arrival. The row's
   * `<tr>` gets `.bd-folder-table__row--highlighted` for the duration of the
   * caller's animation; the parent clears the signal when the flash is done.
   */
  public readonly highlightedId = input<string | null>(null);
  public readonly menuItemsProvider = input<(item: FolderItem) => MenuItem[]>(
    () => [],
  );
  public readonly itemClick = output<FolderItem>();
  /**
   * The complete new selection. With a controlled {@link selection} that means
   * *including* the picks this table does not list — the merge happens here, so
   * a parent can pass the payload straight to its owner (see
   * {@link _emitSelection}).
   */
  public readonly selectionChange = output<FolderItem[]>();
  public readonly labelClick = output<ShareableLabel>();
  public readonly sortChange = output<FolderTableSortChange>();
  public readonly parentNavigate = output<void>();

  /**
   * The table's own selection, used whenever {@link selection} is not set — and
   * written *only* from inside this component (see {@link _emitSelection} and
   * {@link clearSelection}), so the checkbox state has exactly one owner in
   * either mode.
   */
  public readonly selectedItems = signal<FolderItem[]>([]);
  public readonly activeRowMenuItems = signal<MenuItem[]>([]);
  public readonly skeletonRows = [0, 1, 2];

  /**
   * The picked values of the Typ filter, as {@link FolderItem.typeLabel}
   * strings. Empty means "no filter". Written only through
   * {@link setTypeFilter}, which is what keeps the selection reset tied to
   * every change.
   */
  public readonly typeFilterValues = signal<readonly string[]>([]);

  public readonly typeFilterActive = computed(
    () => this.typeFilterValues().length > 0,
  );

  /**
   * The offered values. Counted over the *unfiltered* {@link items} — counting
   * the filtered rows would collapse the list to the one value already picked
   * and leave no way back.
   */
  public readonly typeFilterOptions = computed<TypeFilterOption[]>(() =>
    typeFilterOptionsFor(this.items(), this.typeFilterValues()),
  );

  /**
   * Whether the filter trigger is rendered at all: opted in, on a shown Typ
   * column, and only where there is something to choose between. A single
   * option stays hidden — but an *active* filter keeps its trigger even then,
   * so navigating into a one-type folder cannot hide the way out.
   */
  public readonly typeFilterVisible = computed(
    () =>
      this.typeFilter() &&
      this.showType() &&
      (this.typeFilterOptions().length >= 2 || this.typeFilterActive()),
  );

  protected readonly typeFilterOpen = signal(false);

  protected readonly typeFilterSearchable = computed(
    () => this.typeFilterOptions().length > FILTER_THRESHOLD,
  );

  protected readonly typeFilterIcon = computed(() =>
    this.typeFilterActive() ? 'pi pi-filter-fill' : 'pi pi-filter',
  );

  protected readonly typeFilterAriaLabel = computed(() => {
    const count = this.typeFilterValues().length;
    return count > 0 ? `Nach Typ filtern, ${count} aktiv` : 'Nach Typ filtern';
  });

  /** Narrow-layout trigger label; the header trigger is icon-only. */
  protected readonly typeFilterNarrowLabel = computed(() => {
    const count = this.typeFilterValues().length;
    return count > 0 ? `Filter (${count})` : 'Filter';
  });

  protected readonly typeFilterEmptyTitle = computed(
    () => `Keine Ressourcen vom Typ „${this.typeFilterValues().join(', ')}“`,
  );

  public readonly labelFilterActive = computed(
    () => this.labelFilterValue() !== null,
  );

  /**
   * Whether the Label filter's trigger is rendered: opted in, and with either
   * something to choose or an active value. A single option is worth offering
   * here (unlike the Typ filter, where one option means every row carries it):
   * a label sits on some rows, so filtering by it still narrows the list.
   */
  public readonly labelFilterVisible = computed(
    () =>
      this.labelFilter() &&
      (this.labelFilterOptions().length > 0 || this.labelFilterActive()),
  );

  protected readonly labelFilterOpen = signal(false);

  /**
   * The list box wants objects to filter by a field — plain strings would leave
   * `filterBy` nothing to name.
   */
  protected readonly labelFilterOptionItems = computed(() =>
    this.labelFilterOptions().map((label) => ({ label })),
  );

  protected readonly labelFilterSearchable = computed(
    () => this.labelFilterOptions().length > FILTER_THRESHOLD,
  );

  protected readonly labelFilterIcon = computed(() =>
    this.labelFilterActive() ? 'pi pi-filter-fill' : 'pi pi-filter',
  );

  protected readonly labelFilterAriaLabel = computed(() => {
    const value = this.labelFilterValue();
    return value === null
      ? 'Nach Label filtern'
      : `Nach Label filtern, „${value}“ aktiv`;
  });

  /** Narrow-layout trigger label; the header trigger is icon-only. */
  protected readonly labelFilterNarrowLabel = computed(
    () => this.labelFilterValue() ?? 'Label',
  );

  protected readonly labelFilterChipLabel = computed(
    () => `Label: ${this.labelFilterValue() ?? ''}`,
  );

  /**
   * {@link items} narrowed by the Typ filter — the single place the filter is
   * applied, so sorting, selectability and the listed-key bookkeeping below can
   * never disagree about which rows exist.
   *
   * Folders always survive: they are how the user reaches the files underneath,
   * and a folder browser that hides the path to a match is broken. What keeps
   * them from being *selected* along with the filtered files is
   * {@link folderCheckbox}.
   */
  private readonly _filteredItems = computed(() =>
    filterFolderItems(this.items(), this.typeFilterValues()),
  );

  private readonly _selectableItems = computed(() =>
    selectableFolderItems(this._filteredItems(), this.folderCheckbox()),
  );

  /**
   * What the checkboxes reflect: the controlled {@link selection} narrowed to
   * the rows currently listed, or the table's own {@link selectedItems} when
   * uncontrolled. Deriving it (instead of syncing a copy) is what makes the
   * ticks survive an `items` swap: they are recomputed from the owner's list.
   */
  public readonly effectiveSelection = computed<FolderItem[]>(() => {
    const controlled = this.selection();
    if (controlled === undefined) {
      return this.selectedItems();
    }
    return visibleFolderSelection(this._selectableItems(), controlled);
  });

  /**
   * PrimeNG's selection gate, consulted by the header select-all *and* by row
   * toggles — which is what keeps folders out of the selection even though the
   * header checkbox nominally targets every row.
   */
  public readonly rowSelectable = (row: { data: FolderItem }): boolean =>
    this.folderCheckbox() || !row.data.isFolder;

  /** Whether the column headers act as sort controls at all. */
  public readonly sortable = computed(() => this.sortMode() !== 'fixed');

  public readonly sortedItems = computed(() => {
    switch (this.sortMode()) {
      case 'parent':
        // Parent owns the order (server-side sorting); render as provided.
        return this._filteredItems();
      case 'client':
        return sortFolderItemsBy(
          this._filteredItems(),
          this.sortField(),
          this.sortOrder(),
        );
      default:
        return this.sortBy() === 'modifiedDate'
          ? sortFolderItemsByModifiedDate(this._filteredItems())
          : sortFolderItems(this._filteredItems());
    }
  });

  public readonly activeSortField = computed<FolderTableSortBy | undefined>(
    () => (this.sortable() ? this.sortField() : undefined),
  );

  /**
   * What the (persistent, never-recreated — see the template's `#loadingbody`
   * comment) `<p-table>`'s `[value]` binds to: empty while loading, so its
   * `#loadingbody` template takes over and no stale row renders underneath
   * the skeleton.
   */
  protected readonly tableValue = computed<FolderItem[]>(() =>
    this.loading() ? [] : this.sortedItems(),
  );

  public readonly paginate = computed(() => {
    const size = this.pageSize();
    return size !== undefined && size > 0;
  });

  /**
   * `"flex"` makes PrimeNG's scroller fill the flex column it sits in (see the
   * `.p-datatable-scrollable` rules above). Undefined when the table is not
   * scrollable at all, so no height is imposed on the content-sized table.
   */
  public readonly scrollHeight = computed(() =>
    this.fillHeight() ? 'flex' : undefined,
  );

  /**
   * Virtual scrolling needs a scroller of known height to compute its window,
   * so it only applies to a height-filling, unpaginated table. A content-sized
   * one renders all its rows — the callers that opt out cap the list instead
   * (see `limit` on the recent tab).
   */
  public readonly virtualScroll = computed(
    () => this.fillHeight() && !this.paginate(),
  );

  /**
   * True when the leading glyph is identical on every row (a flat list with no
   * folders), which makes the icon column pure decoration. Drives the host
   * class the narrow-layout container query uses to reclaim its 3rem for the
   * name; a mixed list keeps the column so folders stay distinguishable.
   */
  public readonly hasUniformIcons = computed(
    () => !this.sortedItems().some((item) => item.isFolder),
  );

  public readonly rowMenu = viewChild<Menu>('rowMenu');
  // No viewChild for the sort menu: it is toggled from the template via its
  // `#sortMenu` reference. A same-named signal query would be shadowed by that
  // reference inside the template and fail at runtime — which is also why the
  // popover query below carries a different name than its `#typeFilterPopover`.
  private readonly _typeFilterPopover = viewChild<Popover>('typeFilterPopover');
  private readonly _labelFilterPopover =
    viewChild<Popover>('labelFilterPopover');

  /**
   * The sort orders offered by the narrow-layout control, in menu order. Date
   * comes first because the paged, server-sorted lists load newest-first; the
   * folder listings start on "Name A–Z", which the menu check-marks.
   */
  private static readonly SORT_OPTIONS: ReadonlyArray<
    FolderTableSortChange & { label: string; forTypeColumn?: true }
  > = [
    { label: 'Neueste zuerst', field: 'modifiedDate', order: -1 },
    { label: 'Älteste zuerst', field: 'modifiedDate', order: 1 },
    { label: 'Name A–Z', field: 'name', order: 1 },
    { label: 'Name Z–A', field: 'name', order: -1 },
    { label: 'Typ A–Z', field: 'type', order: 1, forTypeColumn: true },
  ];

  private readonly _sortOptions = computed(() =>
    BdFolderTableComponent.SORT_OPTIONS.filter(
      (option) => !option.forTypeColumn || this.showType(),
    ),
  );

  /** Label of the currently applied sort, shown on the narrow sort button. */
  public readonly activeSortLabel = computed(
    () =>
      this._sortOptions().find(
        (option) =>
          option.field === this.sortField() &&
          option.order === this.sortOrder(),
      )?.label ?? 'Sortieren',
  );

  public readonly sortMenuItems = computed<MenuItem[]>(() =>
    this._sortOptions().map((option) => ({
      label: option.label,
      icon:
        option.field === this.sortField() && option.order === this.sortOrder()
          ? 'pi pi-check'
          : 'pi pi-fw',
      command: () =>
        this.applySort({ field: option.field, order: option.order }),
    })),
  );

  private readonly _host = inject(ElementRef<HTMLElement>);
  private readonly _injector = inject(Injector);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _nameResolvers = inject(BD_SHAREABLE_NAME_RESOLVERS, {
    optional: true,
  });

  /**
   * Display names resolved per item id for types whose backend graph node has no
   * meaningful name (see {@link BD_SHAREABLE_NAME_RESOLVERS}). Falls back to the
   * item's own `name` until (and unless) a resolver supplies one. Never evicted:
   * a resource's name is stable for the lifetime of the table, so paging back
   * and forth costs no further lookups.
   */
  private readonly _resolvedNames = signal<ReadonlyMap<string, string>>(
    new Map(),
  );

  /**
   * Ids a resolver has already been asked about — in flight, resolved, or
   * knowingly unresolvable (absent from the resolver's map). Entries are only
   * released when the resolver *errored*, so a transient failure retries on the
   * next `items()` change while a stable "no name for this id" answer does not
   * re-issue a request on every re-render.
   */
  private readonly _requestedIds = new Set<string>();

  constructor() {
    // Resolve display names for items whose type registered a name resolver
    // (e.g. Klia attachments, whose file name lives on the resource, not the
    // shareable graph node). One batched call per type per items() change; ids
    // already asked about are skipped.
    effect(() => {
      const items = this.items();
      const resolvers = this._nameResolvers;
      if (!resolvers || resolvers.size === 0) {
        return;
      }
      untracked(() => this._resolveMissingNames(items, resolvers));
    });

    // When a deep-link arrival lands on this table, scroll the highlighted
    // row into view. `afterNextRender` ensures the `<tr>` exists by the time
    // we query for it; we look it up by the `data-row-id` attribute set in
    // the template.
    effect(() => {
      const id = this.highlightedId();
      if (!id) {
        return;
      }
      afterNextRender(
        () => {
          const host: HTMLElement = this._host.nativeElement;
          const row = host.querySelector<HTMLElement>(
            `tr[data-row-id="${CSS.escape(id)}"]`,
          );
          row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        },
        { injector: this._injector },
      );
    });
  }

  public iconFor(item: FolderItem): string {
    if (item.isFolder) {
      return 'pi pi-folder';
    }
    return 'pi pi-file';
  }

  /**
   * Name to show for a row: a resolver-supplied name when available (see
   * {@link BD_SHAREABLE_NAME_RESOLVERS}), otherwise the backend-provided name.
   */
  public displayName(item: FolderItem): string {
    return this._resolvedNames().get(item.id) ?? item.name;
  }

  /**
   * One resolution pass over the current page: collects the ids no resolver has
   * been asked about yet, grouped by shareable type, and issues a single batched
   * call per type that actually has unresolved ids.
   */
  private _resolveMissingNames(
    items: FolderItem[],
    resolvers: ShareableNameResolvers,
  ): void {
    const pending = new Map<ShareableNameResolver, string[]>();
    for (const item of items) {
      const resolve = item.isFolder ? undefined : resolvers.get(item.type);
      if (!resolve || this._requestedIds.has(item.id)) {
        continue;
      }
      this._requestedIds.add(item.id);
      const ids = pending.get(resolve);
      if (ids) {
        ids.push(item.id);
      } else {
        pending.set(resolve, [item.id]);
      }
    }

    for (const [resolve, ids] of pending) {
      resolve(ids)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (names) => this._mergeResolvedNames(names),
          // A failed batch says nothing about its ids, so let a later items()
          // change ask again.
          error: () => {
            for (const id of ids) {
              this._requestedIds.delete(id);
            }
          },
        });
    }
  }

  private _mergeResolvedNames(names: ReadonlyMap<string, string>): void {
    if (names.size === 0) {
      return;
    }
    const next = new Map(this._resolvedNames());
    for (const [id, name] of names) {
      if (name) {
        next.set(id, name);
      }
    }
    this._resolvedNames.set(next);
  }

  public getPermissionAvatars(item: FolderItem): PermissionAvatar[] {
    return permissionAvatarsFor(item.allowedPermissions);
  }

  /** Beyond-read rights only, for the compact narrow-layout meta line. */
  public getEditingPermissionAvatars(item: FolderItem): PermissionAvatar[] {
    return editingPermissionAvatarsFor(item.allowedPermissions);
  }

  /**
   * Screen-reader text for the meta-line permission glyphs, which are
   * `aria-hidden` decorations on their own. Announces only the highest right —
   * the display names are cumulative sentences ("Kann bearbeiten und löschen"),
   * so joining every glyph's name would repeat itself.
   */
  public editingPermissionsLabel(item: FolderItem): string {
    const avatars = this.getEditingPermissionAvatars(item);
    return avatars[avatars.length - 1]?.tooltip ?? '';
  }

  public onItemClick(item: FolderItem): void {
    if (item.isRecycleBinItem) {
      return;
    }
    this.itemClick.emit(item);
  }

  /**
   * Name-button activation. Stops propagation so the enclosing cell's click
   * handler does not emit a second {@link itemClick} for the same interaction.
   */
  public onNameClick(event: Event, item: FolderItem): void {
    event.stopPropagation();
    this.onItemClick(item);
  }

  public onLabelClick(event: Event, label: ShareableLabel): void {
    event.stopPropagation();
    this.labelClick.emit(label);
  }

  public onSortFunction(event: SortEvent): void {
    // While loading, `[value]` is forced to `[]` and the sortable headers are
    // `pSortableColumnDisabled` — a real click cannot reach this handler, but
    // PrimeNG's own "value changed" effect (`!lazy()`) re-invokes sortSingle()
    // on every `[value]` swap regardless. On a *freshly created* `<p-table>`
    // (which this no longer is across reloads — see the persistent
    // `#loadingbody` template — but defend anyway) that fires before its
    // internal sortOrder has synced from our binding, emitting a spurious
    // event. Dropping every emission while loading is a strictly safe
    // backstop: a real reorder always arrives after loading finishes.
    if (this.loading()) {
      return;
    }
    const field = event.field as FolderTableSortBy | undefined;
    if (!field || !event.order) {
      return;
    }
    const order: 1 | -1 = event.order === 1 ? 1 : -1;
    // PrimeNG re-runs sorting whenever the data array changes. With customSort
    // that re-emits the already-applied sort — ignore it so consumers driving a
    // server-side reload from sortChange don't loop on every data refresh, and
    // so the initial sortField/sortOrder binding is not reported as a change.
    if (field === this.sortField() && order === this.sortOrder()) {
      return;
    }
    this.applySort({ field, order });
  }

  /**
   * Puts a picked sort into effect — from a column header or from the
   * narrow-layout sort menu. In `client` mode this table applies the order, so
   * it also holds it; in `parent` mode the owner writes its own state from
   * {@link sortChange}, and writing it here too would show a sort the parent may
   * not have applied yet.
   */
  public applySort(change: FolderTableSortChange): void {
    if (this.sortMode() === 'client') {
      this.sortField.set(change.field);
      this.sortOrder.set(change.order);
    }
    this.sortChange.emit(change);
  }

  public onRowMenu(event: Event, item: FolderItem): void {
    this.activeRowMenuItems.set(this.menuItemsProvider()(item));
    this.rowMenu()?.toggle(event);
  }

  /**
   * Table-driven selection change (a row checkbox or the header select-all).
   * Folders are stripped when {@link folderCheckbox} is `false` so the emitted
   * payload can never contain one, independent of PrimeNG's internals.
   */
  public onTableSelectionChange(items: FolderItem[]): void {
    const next = selectableFolderItems(items, this.folderCheckbox());
    this._emitSelection(next);
  }

  /**
   * Toggles one row from outside the checkbox column — the row-click shortcut
   * the tabs offer in checkbox mode. Behaves exactly like clicking that row's
   * checkbox, which is why the tabs no longer reach into this table's state to
   * do it themselves.
   */
  public toggle(item: FolderItem): void {
    const current = this.selection() ?? this.selectedItems();
    const next = toggleFolderSelection(current, item);
    this._emitSelection(next, { merge: false });
  }

  /**
   * Reports a new selection, and owns the controlled/uncontrolled split so no
   * caller has to:
   *
   * - **Uncontrolled** — the table *is* the owner: its own signal is updated and
   *   the payload goes out as-is.
   * - **Controlled** — {@link selection} is the owner's, folder- and
   *   page-spanning list, while this table only ever knows its own rows. So a
   *   payload derived from those rows (`merge`) is completed with the picks that
   *   are *not* listed here: without that, ticking a row after walking into a
   *   folder would silently drop everything picked before it. A payload that was
   *   already computed from the owner's list ({@link toggle}) is complete and
   *   must not be merged again.
   */
  private _emitSelection(
    next: readonly FolderItem[],
    { merge }: { merge: boolean } = { merge: true },
  ): void {
    const controlled = this.selection();
    if (controlled === undefined) {
      this.selectedItems.set([...next]);
      this.selectionChange.emit([...next]);
      return;
    }
    this.selectionChange.emit(
      merge
        ? mergeFolderSelection(this._filteredItems(), controlled, next)
        : [...next],
    );
  }

  /**
   * Drops the table's own selection. Has no visible effect while
   * {@link selection} is controlled — there the owner decides what stays
   * ticked, which is exactly what keeps a picker's cross-folder picks alive
   * when a folder change reloads the rows.
   */
  public clearSelection(): void {
    this.selectedItems.set([]);
  }

  /**
   * Puts a set of Typ values into effect. Compared order-insensitively against
   * the current set, so the list box re-emitting the same picks (which it does
   * on every model write-back) is not mistaken for a change — a spurious change
   * would drop the user's row selection for nothing.
   */
  public setTypeFilter(values: readonly string[]): void {
    if (sameTypeFilterValues(values, this.typeFilterValues())) {
      return;
    }
    this.typeFilterValues.set([...values]);
    this._afterTypeFilterChange();
  }

  /** Closes the Typ filter panel if it is open; a no-op otherwise. */
  protected closeTypeFilter(): void {
    if (this.typeFilterOpen()) {
      this._typeFilterPopover()?.hide();
    }
  }

  /** Drops the Typ filter, showing every row again. */
  public clearTypeFilter(): void {
    this.setTypeFilter([]);
  }

  /**
   * Reports a Label pick upwards. Only on a real change: the list box writes
   * its model back on every interaction, and each reported change costs the
   * parent a request.
   */
  public setLabelFilter(value: string | null | undefined): void {
    const next = value ?? null;
    if (next === this.labelFilterValue()) {
      return;
    }
    this.labelFilterChange.emit(next);
  }

  /** Drops the Label filter — the panel's reset and the caption chip. */
  public clearLabelFilter(): void {
    this.setLabelFilter(null);
  }

  /** Closes whichever column filter panel is open; a no-op otherwise. */
  protected closeColumnFilters(): void {
    this.closeTypeFilter();
    if (this.labelFilterOpen()) {
      this._labelFilterPopover()?.hide();
    }
  }

  /** Removes one value — the caption chip's remove button. */
  protected removeTypeFilterValue(label: string): void {
    this.setTypeFilter(
      this.typeFilterValues().filter((value) => value !== label),
    );
  }

  private _afterTypeFilterChange(): void {
    // The popover is rendered in the branch that renders the table. A filter
    // that leaves no row at all swaps that branch for the empty-state notice
    // mid-interaction, so PrimeNG's onHide never fires and the trigger would
    // come back claiming to be expanded.
    if (this.sortedItems().length === 0) {
      this.typeFilterOpen.set(false);
    }
    // A tick that hides a selected row must not leave it counted in the
    // parent's action bar ("Verschieben (n)"), so the selection goes with the
    // filter change. Only in uncontrolled mode: a controlled selection belongs
    // to its owner, who is free to keep picks this table no longer lists.
    // `clearSelection` alone would not do — the parent keeps its own copy of
    // the last emitted payload, so the reset has to be reported.
    if (this.selection() !== undefined) {
      return;
    }
    this.selectedItems.set([]);
    this.selectionChange.emit([]);
  }
}
